import { Capacitor } from "@capacitor/core";
import { resolveDisplayCategory } from "../apps/categoryNormalization";
import { NativeScreenTime, NativeUsageStats, type UsageSnapshotInput } from "./plugins";
import { todayKey } from "../format";

const emptyTimeBands: UsageSnapshotInput["timeBands"] = [
  { key: "morning", seconds: 0 },
  { key: "afternoon", seconds: 0 },
  { key: "evening", seconds: 0 },
  { key: "night", seconds: 0 },
];

export function unavailableUsageSnapshot(
  reason: "unavailable" | "denied" = "unavailable",
  diagnosticReason?: string,
): UsageSnapshotInput {
  return {
    date: todayKey(),
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC",
    totalSeconds: 0,
    topApps: [],
    timeBands: emptyTimeBands,
    permission: reason,
    reason: diagnosticReason,
  };
}

export type UsagePermissionStatus = "granted" | "denied" | "notDetermined" | "unavailable";

export type UsagePermissionState = {
  status: UsagePermissionStatus;
  requiresEntitlement?: boolean;
  canOpenSettings: boolean;
  reason?: string;
  rawAuthorizationStatus?: string;
  hasDataAccess?: boolean;
  /** Android: raw AppOps mode for diagnostics. */
  appOpsMode?: string;
  /** Android: APK not installed from an app store. */
  isSideloaded?: boolean;
  /** Android 13+: Usage Access toggle likely blocked behind "Allow restricted settings". */
  restrictedSettingsLikely?: boolean;
};

export function snapshotPermissionFromNative(status: UsagePermissionStatus): UsageSnapshotInput["permission"] {
  if (status === "notDetermined") return "denied";
  return status;
}

export async function getUsagePermissionState(): Promise<UsagePermissionState> {
  if (!Capacitor.isNativePlatform()) {
    return { status: "unavailable", canOpenSettings: false, reason: "web" };
  }
  const platform = Capacitor.getPlatform();
  if (platform === "android") {
    const state = await NativeUsageStats.getPermissionState();
    return {
      status: state.status,
      canOpenSettings: state.canOpenSettings,
      appOpsMode: state.appOpsMode,
      isSideloaded: state.isSideloaded,
      restrictedSettingsLikely: state.restrictedSettingsLikely,
    };
  }
  if (platform === "ios") {
    try {
      const state = await NativeScreenTime.getPermissionState();
      return {
        status: state.status,
        requiresEntitlement: state.requiresEntitlement,
        canOpenSettings: true,
        reason: state.reason,
        rawAuthorizationStatus: state.rawAuthorizationStatus,
        hasDataAccess: state.hasDataAccess,
      };
    } catch (error) {
      console.error("NativeScreenTime.getPermissionState failed", error);
      return { status: "unavailable", canOpenSettings: true, reason: "plugin_unregistered" };
    }
  }
  return { status: "unavailable", canOpenSettings: false };
}

/**
 * iOS: presents Apple's Screen Time / App & Website Usage authorization flow.
 * Android: opens system Usage Access settings (no runtime dialog exists for
 * PACKAGE_USAGE_STATS). Permission state is re-read after return/resume.
 */
export async function requestUsagePermission(): Promise<UsagePermissionState> {
  const platform = Capacitor.getPlatform();
  if (platform === "android") {
    try {
      await openUsagePermissionSettings();
    } catch (error) {
      console.error("NativeUsageStats.openUsageSettings failed", error);
    }
    return getUsagePermissionState();
  }
  if (platform !== "ios") {
    return getUsagePermissionState();
  }
  try {
    const state = await NativeScreenTime.requestPermission();
    return {
      status: state.status,
      requiresEntitlement: state.requiresEntitlement,
      canOpenSettings: true,
      reason: state.reason,
      rawAuthorizationStatus: state.rawAuthorizationStatus,
      hasDataAccess: state.hasDataAccess,
    };
  } catch (error) {
    console.error("NativeScreenTime.requestPermission failed", error);
    return { status: "unavailable", canOpenSettings: true, reason: "plugin_unregistered" };
  }
}

function enrichAndroidSnapshot(snapshot: UsageSnapshotInput, collectDurationMs: number): UsageSnapshotInput {
  return {
    ...snapshot,
    collectDurationMs,
    pipelineSummary: [
      "platform=android",
      `permission=${snapshot.permission}`,
      `totalSeconds=${snapshot.totalSeconds}`,
      `topAppCount=${snapshot.topApps.length}`,
      snapshot.reason ? `reason=${snapshot.reason}` : null,
    ].filter(Boolean).join(" "),
  };
}

export async function collectTodayUsage(): Promise<UsageSnapshotInput> {
  if (!Capacitor.isNativePlatform()) {
    return unavailableUsageSnapshot();
  }
  const platform = Capacitor.getPlatform();
  const startedAt = Date.now();
  try {
    if (platform === "android") {
      // Android UsageStatsManager reports per-app foreground time only; there is no
      // segment-level device total equivalent to iOS Settings. Totals may under-count
      // after reinstall if the native layer sums apps — fix belongs in screen0-android.
      const snapshot = await NativeUsageStats.collectToday();
      return enrichAndroidSnapshot(normalizeUsageSnapshot(snapshot), Date.now() - startedAt);
    }
    if (platform === "ios") return normalizeUsageSnapshot(await NativeScreenTime.collectToday());
  } catch (error) {
    console.error("Native usage collection failed", error);
    return {
      ...unavailableUsageSnapshot("unavailable", "plugin_unregistered"),
      collectDurationMs: Date.now() - startedAt,
    };
  }
  return unavailableUsageSnapshot();
}

/**
 * Collects a completed past calendar day from native (iOS 26.4+ builds;
 * Android builds with collectDay). Returns null when unsupported (web, or a
 * build without collectDay) so callers can skip history sync without
 * special-casing.
 */
export async function collectUsageForDay(date: string): Promise<UsageSnapshotInput | null> {
  if (!Capacitor.isNativePlatform()) return null;
  const platform = Capacitor.getPlatform();
  const startedAt = Date.now();
  try {
    if (platform === "ios") {
      return normalizeUsageSnapshot(await NativeScreenTime.collectDay({ date }));
    }
    if (platform === "android") {
      const snapshot = await NativeUsageStats.collectDay({ date });
      return enrichAndroidSnapshot(normalizeUsageSnapshot(snapshot), Date.now() - startedAt);
    }
    return null;
  } catch (error) {
    console.error("Native past-day usage collection failed", date, error);
    return null;
  }
}

function normalizeUsageAppCategory(
  appIdentifier: string | undefined,
  category: string | undefined,
): string | undefined {
  return resolveDisplayCategory(appIdentifier, category);
}

export function normalizeUsageSnapshot(snapshot: UsageSnapshotInput): UsageSnapshotInput {
  if (snapshot.topApps) {
    return {
      ...snapshot,
      topApps: snapshot.topApps.map((app) => ({
        ...app,
        category: normalizeUsageAppCategory(app.appIdentifier, app.category),
      })),
    };
  }
  return {
    ...snapshot,
    topApps: (snapshot.apps ?? []).map((app) => ({
      appIdentifier: app.packageName,
      displayName: app.name,
      durationSeconds: app.seconds,
      color: app.color,
      category: normalizeUsageAppCategory(app.packageName, app.category),
    })),
  };
}

export async function openUsagePermissionSettings() {
  const platform = Capacitor.getPlatform();
  if (platform === "android") {
    await NativeUsageStats.openUsageSettings();
    return;
  }
  if (platform === "ios") {
    await NativeScreenTime.openSettings();
  }
}

/**
 * Android-only: opens Screen0's App info page, where the ⋮ menu hosts
 * "Allow restricted settings" (required before Usage Access can be granted on
 * sideloaded installs, Android 13+). No-ops on builds without the method.
 */
export async function openAndroidAppInfo() {
  if (Capacitor.getPlatform() !== "android") return;
  try {
    await NativeUsageStats.openAppInfo?.();
  } catch (error) {
    console.error("NativeUsageStats.openAppInfo failed", error);
  }
}

export const diagnosticZeroReasons = [
  "no_post_grant_usage",
  "no_usage_recorded_today",
  "report_extension_not_invoked",
  "report_extension_not_embedded",
  "report_view_not_mounted",
  "extension_snapshot_missing",
  "usage_events_unavailable",
  "apps_not_visible",
  "device_locked_no_data",
] as const;

export function isDiagnosticZeroReason(reason?: string): boolean {
  return reason !== undefined && (diagnosticZeroReasons as readonly string[]).includes(reason);
}

export function usageUnavailableMessage(
  permission: UsageSnapshotInput["permission"] | "notDetermined",
  reason?: string,
): string {
  if (permission === "notDetermined") {
    return "Screen Time access is needed. Tap below and approve Apple's \u201CApp & Website Usage\u201D request \u2014 that is iOS's name for Screen Time data access. Heads-up: while Screen0 has access, iOS may show its own Screen Time reports in Settings as off \u2014 turning them back on disconnects Screen0.";
  }
  if (permission === "denied") {
    if (reason === "authorization_denied") {
      return "Screen Time access is denied. Tap below to request access again and approve Apple's \u201CApp & Website Usage\u201D dialog.";
    }
    return "Screen Time access is denied. Turn on Screen Time in Settings \u2192 Screen Time, then grant access in Screen0.";
  }
  if (reason === "plugin_unregistered") {
    return "Screen Time native bridge is not registered in this build. Reinstall from a build that ran cap:sync.";
  }
  if (reason?.startsWith("entitlement_")) {
    return "Screen Time requires Apple's Family Controls entitlement in this build's provisioning profile.";
  }
  if (reason === "report_extension_empty") {
    return "Screen Time access is granted, but today's report returned no data yet. Use other apps, then reopen Screen0.";
  }
  if (reason === "no_post_grant_usage") {
    return "Screen Time access is granted. No usage has been recorded since permission was granted.";
  }
  if (reason === "no_usage_recorded_today") {
    return "Screen Time access is granted. No usage has been recorded yet today.";
  }
  if (reason === "authorization_status_unrecognized_after_approval") {
    return "Screen Time access was approved, and Screen0 is using the newer App and Website Usage authorization path.";
  }
  if (reason === "authorization_status_unrecognized") {
    return "Screen Time returned a newer authorization state. Tap request access to continue with Apple's App and Website Usage flow.";
  }
  if (reason === "report_view_not_mounted") {
    return "Screen Time access is granted, but Screen0 could not mount the native Device Activity report host.";
  }
  if (reason === "report_extension_not_embedded") {
    return "Screen Time access is granted, but Screen0 could not verify the usage report extension in this build. Reinstall from the latest TestFlight build.";
  }
  if (reason === "report_extension_not_invoked") {
    return "Screen Time permission is granted, but iOS did not run the report extension while the native report host was mounted.";
  }
  if (reason === "extension_snapshot_missing") {
    return "The report extension started, but Screen0 could not read the app-group snapshot file.";
  }
  if (reason === "data_access_not_granted") {
    return "Screen Time is connected, but Apple's \u201CApp & Website Usage\u201D data permission is still missing. Open Settings and request Screen Time access, then approve the dialog.";
  }
  if (reason === "ios_data_export_requires_26_4") {
    return "Apple only lets apps read Screen Time data on iOS 26.4 or later. Update iOS to see usage in Screen0.";
  }
  if (reason === "data_export_unavailable") {
    return "Apple currently allows Screen Time data export only on devices in the EU. Screen0 cannot read usage data on this device.";
  }
  if (reason === "activity_data_fetch_failed") {
    return "iOS could not return Screen Time data this time. Use your phone for a few minutes, then switch back to Today or reopen Screen0.";
  }
  if (reason === "usage_events_unavailable") {
    return "Usage access is on, but Android is not returning any usage data to Screen0. Turn the Usage access toggle off and on again in Settings, then reopen Screen0. On sideloaded installs you may first need App info → ⋮ → Allow restricted settings.";
  }
  if (reason === "apps_not_visible") {
    return "Android returned usage activity, but Screen0 could not identify the apps. Reinstall Screen0 from the latest build and try again.";
  }
  if (reason === "device_locked_no_data") {
    return "Android returned no usage data because the device was locked. Unlock the device and reopen Screen0.";
  }
  return "Usage collection is unavailable on this device.";
}
